import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, NetworkError } from '../lib/api';
import { DB_UNAVAILABLE, DB_UNAVAILABLE_WRITE_EVENT } from '../lib/dbUnavailable';
import {
  bookingsScopeKey, getBookingsReadStamp, logStorageEstimate, readCoveringBookings, readReference,
  saveBookingsScope, saveReference, getBookingsCursor, setBookingsCursor,
} from '../lib/offline/cache';
import { fetchChangesSince } from '../lib/offline/changefeed';
import { rollingPrefetch, noteReferenceWriteThrough } from '../lib/offline/prefetch';
import { rollingWindow, homeToday } from '../lib/offline/windowMath';
import { OfflineCachePanel } from '../components/ops-board/OfflineCachePanel';
import { drainOpQueue, enqueueOp, listQueuedOps, subscribeOpQueue, getDeviceId } from '../lib/offline/opQueue';
import { stalenessFor } from '../lib/offline/staleness';
import { purgeDevice } from '../lib/offline/profiles';
import { fetchDeviceStatus } from '../lib/devices';
import { overlayQueuedOps } from '../lib/offline/overlay';
import { subscribeConnection, getConnectionOnline } from '../lib/connection';
import { notifyWarning } from '../lib/notifyWarning';
import type { QueuedOp } from '../lib/offline/db';
import { useAuth } from '../context/AuthContext';
import {
  C, F, addDays, dateKeyInTz, dayMonthLabel, todayInTz, weekdayLabel, zonedDayRangeUtc,
} from '../components/ops-board/theme';
import { Spinner, ErrorBox, WarningsBanner } from '../components/ops-board/atoms';
import BookingForm from '../components/ops-board/BookingForm';
import BookingDetail from '../components/ops-board/BookingDetail';
import SeriesActionModal, { type SeriesAction } from '../components/ops-board/SeriesActionModal';
import DayView, { DayViewLegend, defaultBoardScrollLeft } from '../components/ops-board/DayView';
import { resolveChipChannels } from '../lib/opsBoard/chipChannels';
import WeekView from '../components/ops-board/WeekView';
import RelocateModal from '../components/ops-board/RelocateModal';
import { OfflineDataBanner, OfflineEmptyState } from '../components/ops-board/OfflineBanner';
import AppHeader from '../components/AppHeader';
import ConflictResolver from '../components/ops-board/ConflictResolver';
import OwnConflictPopup from '../components/ops-board/OwnConflictPopup';
// Notifications bell is now GLOBAL (AppHeader). The board keeps only open-conflicts (for its
// overlays + the resolver) — the notification fetch/poll lives in the shared NotificationsContext.
// The board reads that SAME shared state (no second fetch) to drive the own-booking interrupt.
import { fetchOpenConflicts, type ConflictItem } from '../lib/conflicts';
import { useNotifications } from '../context/NotificationsContext';
import { OutsideHoursPopup, WaitlistPopup, MaintenanceSummary, aircraftStatus } from '../components/ops-board/panels';
import { useIsMobile } from '../lib/useIsMobile';
import type {
  Aircraft, Asset, Base, Booking, BookingFormSeed, BookingKind, CompanySettings, ReasonCode, RowFilters, Staff, Student, TrainingActivity,
} from '../lib/opsBoardTypes';
import { buildCopyDraft } from '../lib/opsBoard/buildCopyDraft';

interface RefData {
  bases: Base[]; aircraft: Aircraft[]; assets: Asset[]; staff: Staff[];
  students: Student[]; bookingKinds: BookingKind[]; activities: TrainingActivity[];
  settings: CompanySettings | null;
}

// 0.12.10 Part B — structural sharing for reference reloads. A reload that returns the same data
// must leave `ref`, and every unchanged slice, IDENTICAL. loadBookings and homeWindow key on the
// bases slice. Before this, every reload minted a new `ref`, which re-created loadBookings (the
// debounced effect refetched bookings) and recomputed homeWindow (a forced rolling prefetch
// refetched them again): one save became three or more GET /api/bookings, measured on prod
// 2026-09-22/23. Same byte-compare idea as refreshAircraftSlice's bail-out (PR-000044/040).
function shareUnchangedSlices(prev: RefData | null, next: RefData): RefData {
  if (!prev) return next;
  const keep = <K extends keyof RefData>(k: K): RefData[K] =>
    JSON.stringify(prev[k]) === JSON.stringify(next[k]) ? prev[k] : next[k];
  const merged: RefData = {
    bases: keep('bases'), aircraft: keep('aircraft'), assets: keep('assets'), staff: keep('staff'),
    students: keep('students'), bookingKinds: keep('bookingKinds'), activities: keep('activities'),
    settings: keep('settings'),
  };
  return (Object.keys(merged) as (keyof RefData)[]).every((k) => merged[k] === prev[k]) ? prev : merged;
}

// 0.12.10 addendum: shown when the silent refetch after a save fails (see loadBookings `afterSave`).
const STALE_AFTER_SAVE = 'Saved. The board could not refresh just now, so other bookings may be out of date. It will retry automatically, or use ⟳ Refresh.';

const POLL_MS = 45_000; // board liveness refetch (Step 5c.3, Ben-approved)
// Reference-data fan-out. PERF PART B: prod DATABASE_URL confirmed by Ben from Railway on 2026-09-15 as
// `?pgbouncer=true&connection_limit=10&pool_timeout=10`. It was 3 on the belief the backend ran
// connection_limit=1 (a real wedge incident at that value). 6, not 10, because the board fires bookings,
// conflicts, sync and notifications concurrently from the same pool. Mirrors REF_BATCH in offline/prefetch.ts.
const REF_BATCH_SIZE = 6;
const BOOKINGS_DEBOUNCE_MS = 250; // coalesce rapid date/view changes into ONE bookings fetch
                                  // (HANDOFF S24: rapid refetch+abort churn strands DB connections)

type ViewMode = 'day' | 'week';

const ALL_BASES = '__all__';

function mondayOf(dateStr: string): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
  return addDays(dateStr, -((dow + 6) % 7));
}

function bookingEndMs(b: Booking): number {
  const s = new Date(b.block_start_at!).getTime();
  if (b.arr_time_planned) return new Date(b.arr_time_planned).getTime();
  return s + (b.activity_mins ?? 60) * 60_000;
}

// ── Step 5d Phase 1: offline read cache plumbing ──────────────────────────────

// Write-through of a successful reference load (merge semantics — see lib/offline/cache).
// Fire-and-forget: cache writes must never delay or break the online board.
// PERF PART A: the in-flight writes are REGISTERED (not awaited) with the prefetch, so its unforced
// reference pass can see this load's freshness stamp instead of fetching all eight again.
function saveRefDataToCache(data: RefData): void {
  noteReferenceWriteThrough(Promise.all([
    saveReference('bases', data.bases, (b) => b.id),
    saveReference('aircraft', data.aircraft, (a) => a.registration), // no id — registration IS the key
    saveReference('assets', data.assets, (a) => a.id),
    saveReference('staff', data.staff, (s) => s.id),
    saveReference('students', data.students, (s) => s.id),
    saveReference('booking-kinds', data.bookingKinds, (k) => k.id),
    saveReference('training-activities', data.activities, (a) => a.id),
    data.settings ? saveReference('company-settings', [data.settings], () => 'singleton') : Promise.resolve(),
  ]));
}

// Offline fallback: rebuild RefData from the cache. Null when the device has never
// successfully loaded the board (bases are the structural minimum — without them
// nothing can render honestly).
async function readRefDataFromCache(): Promise<RefData | null> {
  const [bases, aircraft, assets, staff, students, kinds, activities, settings] = await Promise.all([
    readReference<Base>('bases'), readReference<Aircraft>('aircraft'), readReference<Asset>('assets'),
    readReference<Staff>('staff'), readReference<Student>('students'), readReference<BookingKind>('booking-kinds'),
    readReference<TrainingActivity>('training-activities'), readReference<CompanySettings>('company-settings'),
  ]);
  if (!bases || bases.items.length === 0) return null;
  return {
    bases: bases.items, aircraft: aircraft?.items ?? [], assets: assets?.items ?? [],
    staff: staff?.items ?? [], students: students?.items ?? [],
    bookingKinds: kinds?.items ?? [], activities: activities?.items ?? [],
    settings: settings?.items[0] ?? null,
  };
}

export default function OpsBoard() {
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Shared notifications (same instance the header bell uses — no second fetch). Used here only
  // to drive the own-booking conflict interrupt.
  const { notifications, markRead, dismiss } = useNotifications();

  const canWorkflow = useMemo(
    () => (user?.roles ?? []).some((r) => r === 'ops_controller' || r === 'admin'),
    [user],
  );
  // Booking-rights (instructor/ops/admin) — drives the per-policy waitlist allocate affordance.
  const canBook = useMemo(
    () => (user?.roles ?? []).some((r) => r === 'instructor' || r === 'ops_controller' || r === 'admin'),
    [user],
  );
  // Single-booking / single-occurrence cancel is open to ALL staff (Ben 2026-07-10; audited, row
  // preserved). Every role that can load the ops board is a staff role (see App.tsx route guard /
  // backend STAFF_ROLE_NAMES), so this is true for any board session — kept as an explicit prop,
  // derived like canWorkflow, so BookingDetail's cancel affordance is precise. Bulk series cancel
  // stays canWorkflow.
  const canCancel = useMemo(
    () => (user?.roles ?? []).some((r) =>
      ['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager'].includes(r)),
    [user],
  );
  // Approve / decline / WITHDRAW APPROVAL is open to ALL staff (Ben 2026-08-25, LOCKED) — mirrors
  // the backend APPROVAL_ROLES = STAFF_ROLE_NAMES. Same set as canCancel today, but a separate memo
  // because they are two independent decisions: the person who can answer "am I available" is the
  // person on the booking, and an approval nobody can withdraw is worse than no approval.
  const canApprove = useMemo(
    () => (user?.roles ?? []).some((r) =>
      ['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager'].includes(r)),
    [user],
  );
  // Recording "client advised of changes" is open to ALL staff (Ben 2026-09-16) — mirrors the
  // backend CLIENT_ADVISED_ROLES = STAFF_ROLE_NAMES. Same set as canCancel / canApprove today and,
  // like them, a separate memo because it is its own decision. ⚠ IT IS NOT canBook: the maintenance
  // displacement notice asks the senior_base_pilot to ring the client when the instructor cannot,
  // and senior_base_pilot / pilot / haamc are not in the booking-write tier — gating the button on
  // canBook would tell them to make the call and then hide the control that records it.
  const canMarkAdvised = useMemo(
    () => (user?.roles ?? []).some((r) =>
      ['pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager'].includes(r)),
    [user],
  );
  // Maintenance-booking edit tier (PR-000038, Ben 2026-07-22) — mirrors the backend
  // MAINTENANCE_EDIT_ROLES: senior roles only, regular instructors/pilots excluded. Drives
  // BookingDetail's Edit affordance on maintenance bookings so a user never opens an edit
  // the server would 403.
  const canEditMaintenance = useMemo(
    () => (user?.roles ?? []).some((r) =>
      ['senior_base_pilot', 'ops_manager', 'ops_controller', 'haamc', 'admin'].includes(r)),
    [user],
  );
  // 5d Phase 6: discreet entry to the admin-only beta instrumentation console.
  const isAdmin = useMemo(() => (user?.roles ?? []).includes('admin'), [user]);

  // ── Reference data ──────────────────────────────────────────────────────────
  const [ref, setRef] = useState<RefData | null>(null);
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError] = useState<string | null>(null);
  // Coded reason lists (Feature C) — ONLINE-ONLY, kept in memory (NOT written to the IndexedDB
  // reference cache, to avoid touching the offline stack). A fresh offline load has empty pickers.
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  // Offline + nothing cached on this device → whole-board honest empty state (5d Ph.1).
  const [refNoCache, setRefNoCache] = useState(false);
  // Stage 1.5: loadRefData's 429 back-off needs "is a board already on screen" without a
  // stale closure (its useCallback deps are []) — the usual ref-mirror pattern.
  const refPresentRef = useRef(false);
  useEffect(() => { refPresentRef.current = ref !== null; }, [ref]);

  const loadRefData = useCallback(async () => {
    setRefLoading(true);
    setRefError(null);
    try {
      // Throttled fan-out (Step: JSON-error fix). The original reason was a real incident: on Prisma
      // connection_limit=1, firing all 8 reference fetches at once piled requests on the single
      // connection and wedged it (→ empty 500s). Prod was confirmed on 2026-09-15 to run
      // connection_limit=10 (PERF PART B), so the batch is REF_BATCH_SIZE (6) — still under the limit,
      // with headroom for the board's other concurrent requests. Order preserved; same data.
      const reqs = [
        () => apiFetch<Base[]>('/api/bases'),
        () => apiFetch<Aircraft[]>('/api/aircraft'),
        () => apiFetch<Asset[]>('/api/assets'),
        () => apiFetch<Staff[]>('/api/staff'),
        () => apiFetch<Student[]>('/api/students'),
        () => apiFetch<BookingKind[]>('/api/booking-kinds'),
        () => apiFetch<TrainingActivity[]>('/api/training-activities'),
        () => apiFetch<CompanySettings>('/api/company-settings'),
      ] as const;
      const results: Array<{ data: unknown; error: { message: string } | null }> = [];
      for (let i = 0; i < reqs.length; i += REF_BATCH_SIZE) {
        const batch = reqs.slice(i, i + REF_BATCH_SIZE).map((fn) => fn());
        results.push(...(await Promise.all(batch)) as typeof results);
      }
      const [bases, aircraft, assets, staff, students, kinds, activities, settings] = results as [
        Awaited<ReturnType<typeof apiFetch<Base[]>>>, Awaited<ReturnType<typeof apiFetch<Aircraft[]>>>,
        Awaited<ReturnType<typeof apiFetch<Asset[]>>>, Awaited<ReturnType<typeof apiFetch<Staff[]>>>,
        Awaited<ReturnType<typeof apiFetch<Student[]>>>, Awaited<ReturnType<typeof apiFetch<BookingKind[]>>>,
        Awaited<ReturnType<typeof apiFetch<TrainingActivity[]>>>, Awaited<ReturnType<typeof apiFetch<CompanySettings>>>,
      ];
      const firstErrRes = [bases, aircraft, assets, staff, students, kinds, activities, settings].find((r) => r.error);
      if (firstErrRes?.error) {
        // Stage 1.5 — 429 back-off: this refresh often runs right AFTER a successful write
        // (onFormSubmitted / onWorkflowChanged / onPeopleChanged), and a rate-limited
        // response must not replace a working board with the full-page error — the save
        // already happened (PR-000032: "booking created, 429 was collateral"). Keep the
        // current slices; the poll's aircraft refresh and the next action self-heal. A
        // FIRST load (nothing on screen yet) keeps the honest error + Retry box.
        if (firstErrRes.status === 429 && refPresentRef.current) return;
        setRefError(firstErrRes.error.message);
        return;
      }
      const data: RefData = {
        bases: bases.data ?? [], aircraft: aircraft.data ?? [], assets: assets.data ?? [],
        staff: staff.data ?? [], students: students.data ?? [], bookingKinds: kinds.data ?? [], activities: activities.data ?? [],
        settings: settings.data ?? null,
      };
      setRef((prev) => shareUnchangedSlices(prev, data));
      setRefNoCache(false);
      saveRefDataToCache(data); // 5d Ph.1 write-through (fire-and-forget)
      // Feature C: coded reason lists — online-only, best-effort, kept in memory (NOT cached).
      void apiFetch<ReasonCode[]>('/api/reason-codes').then((r) => { if (!r.error && r.data) setReasonCodes(r.data); }).catch(() => { /* offline: pickers stay empty */ });
    } catch (e) {
      // Network-LEVEL failure only (fetch rejection): fall back to the offline cache.
      // HTTP error responses (incl. the dev proxy's 500 when the backend is down — S29
      // Part C) take the envelope path above and keep their existing retryable handling.
      if (e instanceof NetworkError) {
        const cached = await readRefDataFromCache();
        if (cached) { setRef(cached); return; } // data-age banner comes from the bookings scope
        setRefNoCache(true);
        return;
      }
      setRefError(e instanceof Error ? e.message : 'Failed to load reference data.');
    } finally {
      setRefLoading(false);
    }
  }, []);

  useEffect(() => { loadRefData(); }, [loadRefData]);

  // M2: keep aircraft maintenance state (serviceable / ECD) LIVE across sessions. The bookings poll
  // re-fetches only bookings; aircraft ref data otherwise loads once at mount, so a maintenance
  // create / confirm-return in ANOTHER session wouldn't surface here (banner / badge / RHS summary)
  // until a reload. Refresh just the aircraft slice on the poll cadence — lightweight
  // (GET /api/aircraft), write-through to the same IndexedDB cache loadRefData uses, and
  // offline-tolerant: a transient server error or network failure keeps the current/cached slice
  // (never clobbers the 5d offline fallback). Same-session create/return already refresh via
  // loadRefData in onFormSubmitted / onWorkflowChanged.
  // Serialized aircraft slice currently held in `ref` — the comparison baseline for the poll's
  // bail-out below. Mirrors ref (both full loads and slice refreshes) via the effect.
  const lastAircraftJsonRef = useRef<string | null>(null);
  useEffect(() => { lastAircraftJsonRef.current = ref ? JSON.stringify(ref.aircraft) : null; }, [ref]);
  const refreshAircraftSlice = useCallback(async () => {
    try {
      const res = await apiFetch<Aircraft[]>('/api/aircraft');
      if (res.error || !res.data) return; // transient server error → keep the current slice
      const aircraft = res.data;
      // PR-000044/040: bail out when the slice is byte-identical to what is already on screen.
      // An unconditional setRef minted a NEW `ref` object every poll, which re-created
      // loadBookings (deps include ref) and re-fired the debounced NON-silent bookings load —
      // the spinner unmounted the board sections and snapped the scroll position back. With
      // this guard an unchanged fleet leaves `ref` untouched, so a silent poll cannot move
      // the viewport at all.
      if (JSON.stringify(aircraft) === lastAircraftJsonRef.current) return;
      setRef((prev) => (prev ? { ...prev, aircraft } : prev));
      void saveReference('aircraft', aircraft, (a) => a.registration); // mirror loadRefData's write-through
    } catch (e) {
      if (e instanceof NetworkError) return; // offline → keep cached slice (5d contract)
      // best-effort poll refresh: swallow any other error silently
    }
  }, []);

  // ── Board state ───────────────────────────────────────────────────────────────
  const [baseId, setBaseId] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [date, setDate] = useState<string>(() => todayInTz('Australia/Darwin'));
  const [filters, setFilters] = useState<RowFilters>({ aircraft: true, instructors: true, assets: true });
  const [showCancelled, setShowCancelled] = useState(false);

  const allBases = baseId === ALL_BASES;
  const selectedBase = useMemo(() => (allBases ? null : ref?.bases.find((b) => b.id === baseId) ?? null), [ref, baseId, allBases]);
  const formBaseId = useMemo(() => (allBases ? ref?.bases[0]?.id ?? '' : baseId), [allBases, ref, baseId]);
  const anchorTz = selectedBase?.timezone ?? ref?.bases[0]?.timezone ?? 'Australia/Darwin';

  // Full-day board (Stage 2): the track now starts at 00:00, so the FIRST day-view render scrolls
  // the board column to the operating day (selected base's opening time, else the first base's,
  // else 06:00) instead of midnight. Once per board mount — after that the user's pan position is
  // never touched. Runs only once the day sections are actually in the DOM — that needs ref
  // loaded AND the initial scope applied (baseId set, a render after ref arrives); earlier the
  // content isn't wide enough yet, so the assignment would clamp to 0 and the one shot be spent.
  const boardScrollRef = useRef<HTMLDivElement | null>(null);
  const boardScrollDefaulted = useRef(false);
  useEffect(() => {
    if (boardScrollDefaulted.current || !ref || !baseId || viewMode !== 'day') return;
    const el = boardScrollRef.current;
    if (!el) return;
    boardScrollDefaulted.current = true;
    el.scrollLeft = defaultBoardScrollLeft((selectedBase ?? ref.bases[0])?.operating_hours_start);
  }, [ref, baseId, viewMode, selectedBase]);

  // Initial board scope (Ben, 2026-06-10): the user's HOME BASE if set, else the whole-company
  // (all-bases) view — supersedes the old alphabetical bases[0] default. Manual switching is
  // unchanged below.
  useEffect(() => {
    if (ref && ref.bases.length > 0 && !baseId) {
      const home = user?.home_base_id ? ref.bases.find((b) => b.id === user.home_base_id) : undefined;
      if (home) {
        setBaseId(home.id);
        setDate(todayInTz(home.timezone));
      } else {
        setBaseId(ALL_BASES);
        setDate(todayInTz(ref.bases[0].timezone));
      }
    }
  }, [ref, baseId, user]);

  // ── Bookings — ALWAYS fetched tenant-wide (no base_id) so cross-base mirror /
  // visiting rows render identically in single-base and all-bases modes. ───────
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState<string | null>(null);
  // 5d Ph.1 offline read state: non-null while the board is rendering a CACHED scope
  // (drives the persistent data-age banner); offlineEmpty = offline + scope never cached.
  const [offlineInfo, setOfflineInfo] = useState<{ refreshedAt: string } | null>(null);
  const [offlineEmpty, setOfflineEmpty] = useState(false);
  // Board liveness (Step 5c.3): polling refetch, "last updated" stamp, change highlight.
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [changedIds, setChangedIds] = useState<Set<string>>(new Set());
  const updatedAtRef = useRef<Map<string, string>>(new Map()); // booking id → updated_at (previous fetch)
  const abortRef = useRef<AbortController | null>(null);
  const showFormRef = useRef(false); // skip polling while the form is open (don't clobber edits)
  // PR-000031: skip polling while an edge-drag / its confirm dialog is live. The poll tick's
  // aircraft-slice refresh replaces `ref`, which re-fires the debounced NON-silent bookings load
  // and unmounts the board sections (spinner) — wiping the in-progress drag state mid-gesture.
  // Same pattern as showFormRef; DayView reports the interaction via onDragActiveChange.
  const dragActiveRef = useRef(false);
  // 0.12.11: the DB_UNAVAILABLE background refresh deferred during a drag runs when the drag ends
  // (drag state is a ref, so no effect would notice). See onDbUnavailableWrite below.
  const dbRefreshPendingRef = useRef(false);
  const dbRefreshRef = useRef(() => {});
  const onDragActiveChange = useCallback((active: boolean) => {
    dragActiveRef.current = active;
    if (!active && dbRefreshPendingRef.current && !showFormRef.current && !overlayOpenRef.current) dbRefreshRef.current();
  }, []);
  // PR-000044/040: skip the poll while ANY overlay interaction is open — BookingDetail panel,
  // relocate modal/prompt, series-action dialog, conflict resolver — not just the form and a
  // live drag. A background refresh must never dismiss or disturb something the user is mid-way
  // through; the skipped tick's data simply arrives on the next unguarded tick (or via the
  // manual Refresh button). Mirrored from state by the effect next to showFormRef below.
  const overlayOpenRef = useRef(false);
  // 5d-p3b: the scope key whose data is currently ON SCREEN. The change-feed "empty poll →
  // skip refetch" optimisation is only safe when the displayed data already IS this scope
  // (a same-window silent poll) — never across a window/date navigation.
  const displayedScopeKeyRef = useRef<string | null>(null);

  // ── 5d-p3a: open-conflicts state (poll-piggyback, no new loop) ──────────────────
  // Notifications moved to the global header (useNotifications); the board still tracks open
  // conflicts for its overlays + the resolver. Declared up here so the polling effect AND the
  // drain can both refresh it.
  const [openConflicts, setOpenConflicts] = useState<ConflictItem[]>([]);
  const [resolverConflictId, setResolverConflictId] = useState<string | null>(null);
  const [connectionOnline, setConnectionOnline] = useState(getConnectionOnline());
  useEffect(() => subscribeConnection(setConnectionOnline), []);
  const refreshOpenConflicts = useCallback(async () => {
    setOpenConflicts(await fetchOpenConflicts());
  }, []);

  // Deep-link from the global notifications bell: /ops-board?conflict=<id> opens the resolver,
  // then the param is cleared so a refresh/back doesn't re-open it.
  useEffect(() => {
    const cid = searchParams.get('conflict');
    if (!cid) return;
    setResolverConflictId(cid);
    const next = new URLSearchParams(searchParams);
    next.delete('conflict');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Deep-link guard: the booking id this session has already opened from ?booking=, so the handler
  // runs exactly once per id however many times its effect re-enters. See that effect for why a
  // cleanup-cancellation cannot do this job.
  const handledBookingParam = useRef<string | null>(null);
  // ── Deep link to ONE booking: /ops-board?booking=<id> (inventory item 14) ────────────────────
  // NO NEW ROUTE. /ops-board/:id was deliberately not added: the board's state is scope plus date
  // plus view mode, and a second route would have to reproduce all of it. This follows the
  // ?conflict= pattern directly above — read the param, act on it, strip it with replace:true so a
  // refresh or a back-navigation does not re-open it.
  //
  // ⚠ THE SCOPE PROBLEM IS THIS HANDLER'S ALONE, and the conflict handler is NO GUIDE to it: the
  // resolver is a modal that renders independently of what the board is showing, so it never had to
  // care. A booking does. The board is scoped by base (tabs or All bases) and by date, and a
  // deep-linked booking routinely sits outside BOTH — a 1700 escalation email lands the evening
  // before, pointing at tomorrow, possibly at another base.
  //
  // So the handler MOVES THE BOARD to the booking rather than hoping it is already there:
  //   • base  → the booking's own base when it is one this board can show, else All bases. All
  //             bases is the honest fallback for a base that is missing or deactivated: it can
  //             still render the booking, where a dangling base id would render an empty board.
  //   • date  → the booking's local day, computed in the timezone the BOARD will actually use once
  //             the base is switched (mirroring anchorTz), NOT in the booking's dep_timezone. Those
  //             are normally identical, since dep_timezone is copied from the base at create, but
  //             they diverge if a base's timezone was later changed, and it is the board's own
  //             anchor that decides which column a booking lands in.
  //   • view  → day, because a single booking is what is being pointed at.
  //
  // The detail panel does NOT depend on any of that: `selected` is plain state and the fetch is by
  // id, so the panel opens even for a booking outside the loaded set. Moving the board is about
  // what the user sees behind the panel when they close it.
  useEffect(() => {
    const bid = searchParams.get('booking');
    // Wait for `ref`: the base lookup needs it, and waiting also lets the initial-scope effect
    // above settle first — the async fetch below then resolves after it, so these writes land last.
    if (!bid || !ref) return;
    // ⚠ RUN ONCE PER ID, VIA A REF — and NOT via an effect-cleanup cancellation, which is the
    // obvious thing to reach for here and is self-defeating. Found in a browser check: stripping
    // the param below re-renders with a NEW searchParams identity, so this effect re-runs, its
    // cleanup flips a `live` flag, and the in-flight fetch's result is thrown away. The symptom was
    // three 200 responses for the booking and a panel that never opened.
    //
    // The ref also covers the honest re-entry case: setSearchParams is not synchronous, so several
    // renders can still see the old param before the URL updates.
    if (handledBookingParam.current === bid) return;
    handledBookingParam.current = bid;
    // Strip the param so a refresh or a back-navigation does not re-open the panel.
    const next = new URLSearchParams(searchParams);
    next.delete('booking');
    setSearchParams(next, { replace: true });

    void (async () => {
      try {
        const res = await apiFetch<Booking>(`/api/bookings/${bid}`);
        if (res.error || !res.data) {
          setWarnings([`Couldn't open that booking. ${res.error?.message ?? 'It may no longer exist.'}`]);
          return;
        }
        const b = res.data;
        const base = ref.bases.find((x) => x.id === b.base_id) ?? null;
        setBaseId(base ? base.id : ALL_BASES);
        if (b.block_start_at) {
          const boardTz = base?.timezone ?? ref.bases[0]?.timezone ?? b.dep_timezone;
          setDate(dateKeyInTz(b.block_start_at, boardTz));
        }
        setViewMode('day');
        setSelected(b);
      } catch {
        // Offline: the board itself still works, so say what failed rather than blocking it.
        setWarnings(['Offline — that booking could not be opened.']);
      }
    })();
  }, [searchParams, setSearchParams, ref]);

  // 5d Phase 4: a revoked device → purge the local DB + force re-auth (re-enrol online).
  // Declared up here so both the poll (revocation status) and the drain (purge flag) can use it.
  const purgeAndUnenroll = useCallback(async () => {
    await purgeDevice();
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const weekDates = useMemo(() => {
    const mon = mondayOf(date);
    return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
  }, [date]);

  // 5d-p5: the rolling-cache home window (−2/+7d anchored to the home base's LOCAL today,
  // UTC bounds unioned across all bases — same keyspace as the board). null when the user
  // has no home base (the auto window is skipped; pinned scopes + reference still prefetch).
  // 0.12.10 Part B: keyed on the BASES slice, not the whole `ref`. An aircraft-slice refresh (the
  // poll, or a save) must not recompute the window, because that forces a rolling bookings prefetch.
  const refBases = ref?.bases;
  const homeWindow = useMemo(() => {
    if (!refBases || refBases.length === 0) return null;
    const home = user?.home_base_id ? refBases.find((b) => b.id === user.home_base_id) : undefined;
    if (!home) return null;
    return rollingWindow(refBases, homeToday(home.timezone));
  }, [refBases, user]);
  // Stable ref so the poll/reconnect effects don't re-subscribe when the window recomputes.
  const homeWindowRef = useRef(homeWindow);
  useEffect(() => { homeWindowRef.current = homeWindow; }, [homeWindow]);
  // forceReference defaults to `force` — only the mount/window-change pass below separates them.
  const runRollingPrefetch = useCallback((force: boolean, forceReference: boolean = force) => {
    void rollingPrefetch({ homeWindow: homeWindowRef.current, force, forceReference });
  }, []);
  // Initial / window-change pass: force a refresh so departure data is fresh once we have a
  // home window (and whenever it changes). The routine is guarded + offline/locked-skipped.
  // PERF PART A: the BOOKINGS windows stay forced; the REFERENCE half is NOT forced here. homeWindow
  // only exists once loadRefData has just set `ref`, and that same load has already fetched the eight
  // reference endpoints and registered their write-through, so a forced reference pass fetched the
  // identical set a second time (on every board load AND every post-save refetch). The unforced pass
  // waits for that write-through and skips on its fresh stamp. Reconnect (below) still forces both.
  useEffect(() => { if (homeWindow) runRollingPrefetch(true, false); }, [homeWindow, runRollingPrefetch]);

  // 0.12.10 Part B: depends on the bases slice (the only part of `ref` it reads), so a reference
  // reload or an aircraft refresh no longer re-creates it and re-fires the debounced fetch below.
  // 0.12.10 addendum: `afterSave` marks the ONE silent refetch that follows a successful write. A
  // silent load normally fails without a word, which after a save could leave the board wrong with no
  // signal. So: an HTTP error or other failure adds STALE_AFTER_SAVE to the warnings strip (cleared by
  // the next successful load), and a network failure keeps the on-screen rows, which already hold the
  // save's own returned booking, rather than replacing them with the older IndexedDB copy. The offline
  // data-age banner still shows.
  // setWarnings is declared further down; this runs only at call time, after it exists.
  const flagStaleAfterSave = () => setWarnings((w) => (w.includes(STALE_AFTER_SAVE) ? w : [...w, STALE_AFTER_SAVE]));
  const loadBookings = useCallback(async (opts: { silent?: boolean; afterSave?: boolean } = {}) => {
    if (!refBases || refBases.length === 0) return;
    if (!opts.silent) setBookingsLoading(true);
    setBookingsError(null);
    // UTC window = union across all bases (multi-timezone), widened a day each side so
    // multi-day bookings overlapping the window are included.
    const ranges = refBases.map((b) =>
      viewMode === 'day'
        ? { from: zonedDayRangeUtc(addDays(date, -1), b.timezone).from, to: zonedDayRangeUtc(addDays(date, 1), b.timezone).to }
        : { from: zonedDayRangeUtc(addDays(weekDates[0], -1), b.timezone).from, to: zonedDayRangeUtc(addDays(weekDates[6], 1), b.timezone).to },
    );
    const from = ranges.map((r) => r.from).sort()[0];
    const to = ranges.map((r) => r.to).sort().slice(-1)[0];
    // Canonical scope key (5d Ph.1) — the board fetches tenant-wide, so base segment = 'all'.
    const scopeKey = bookingsScopeKey(null, from, to);
    abortRef.current?.abort(); // cancel any in-flight refetch
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // ── 5d-p3b: change-feed pre-check (the download half of sync) ──────────────────
    // Read this scope's cursor and ask the feed what `tasks` changed since it. The head is
    // captured HERE — before the payload fetch below (capture-before-fetch): anything landing
    // during the fetch sorts after it and is caught next poll (at worst one idempotent extra
    // refetch, never a missed change). A feed failure degrades to headCursor=null → we just
    // full-refetch, so the feed can only ever SAVE work, never break the online path.
    const priorCursor = await getBookingsCursor(scopeKey);
    if (ctrl.signal.aborted) return; // superseded while reading the cursor
    const feed = await fetchChangesSince(priorCursor);
    if (ctrl.signal.aborted) return; // superseded during the feed call
    // SKIP the payload refetch only when: the feed succeeded, we had an established baseline,
    // nothing changed, AND this is a same-window SILENT poll whose data is already on screen.
    // (A baseline establish — priorCursor null — and any user-initiated load always fetch.)
    const caughtUp = feed.headCursor !== null && priorCursor !== null && feed.changes.length === 0;
    if (caughtUp && opts.silent && displayedScopeKeyRef.current === scopeKey) {
      // The device is genuinely current: advance the cursor + refresh the data-age stamp
      // WITHOUT re-fetching the payload (cache + on-screen data already current).
      await setBookingsCursor(scopeKey, feed.headCursor);
      setLastUpdated(new Date());
      setOfflineInfo(null);
      setOfflineEmpty(false);
      return;
    }
    // Cursor to stamp on the scope after a successful fetch: the head captured above when the
    // feed was trusted; undefined (preserve prior) when the feed failed but we fetched anyway.
    const cursorToStore = feed.headCursor !== null ? feed.headCursor : undefined;

    try {
      const res = await apiFetch<Booking[]>(`/api/bookings?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { signal: ctrl.signal });
      if (res.error) {
        // HTTP error response — existing handling, NOT the offline path (the dev proxy's
        // 500 when the backend is down must not masquerade as "offline", S29 Part C).
        // Stage 1.5 — 429 back-off: if the requested window is ALREADY on screen (the
        // post-save refetch, or a focus burst), keep it silently — the 45 s tick is the
        // retry. A DIFFERENT window (user navigation) falls through to the honest error +
        // Retry: stale chips must never masquerade as the new day's schedule.
        if (opts.afterSave) flagStaleAfterSave();
        if (res.status === 429 && displayedScopeKeyRef.current === scopeKey) return;
        if (!opts.silent) { setBookingsError(res.error.message); setOfflineInfo(null); setOfflineEmpty(false); }
        return;
      }
      const next = res.data ?? [];
      // Highlight bookings whose updated_at changed since the previous fetch.
      const prev = updatedAtRef.current;
      const changed = new Set<string>();
      if (prev.size > 0) {
        for (const b of next) { const p = prev.get(b.id); if (p && p !== b.updated_at) changed.add(b.id); }
      }
      updatedAtRef.current = new Map(next.map((b) => [b.id, b.updated_at]));
      setBookings(next);
      setLastUpdated(new Date());
      displayedScopeKeyRef.current = scopeKey; // on-screen data now IS this scope (5d-p3b)
      if (changed.size > 0) setChangedIds(changed);
      // 5d Ph.1: successful fetch → write-through (replace-by-scope) + banner clears.
      // 5d-p3b: stamp the change-feed head (captured before this fetch) onto the scope.
      setOfflineInfo(null);
      setOfflineEmpty(false);
      setWarnings((w) => (w.includes(STALE_AFTER_SAVE) ? w.filter((m) => m !== STALE_AFTER_SAVE) : w));
      void saveBookingsScope(scopeKey, { from, to }, next, cursorToStore);
      if (!opts.silent) void logStorageEstimate(`bookings ${from}..${to} (${next.length} rows)`);
    } catch (e) {
      if (ctrl.signal.aborted) return; // superseded — ignore
      if (e instanceof NetworkError) {
        // Network-level failure (fetch rejection / no response): render the requested
        // window from IndexedDB with the always-honest data-age banner (v2 §8 passive).
        // 5d-p5: readCoveringBookings reconstructs this narrow window from the freshest
        // CACHED scope that covers it — so a day inside the rolling −2/+7d window renders
        // offline even if it was never opened online (exact scope falls through to covering).
        const cached = await readCoveringBookings(from, to);
        if (ctrl.signal.aborted) return; // superseded while reading the cache
        if (opts.afterSave && displayedScopeKeyRef.current === scopeKey) {
          // The on-screen rows are this scope's last fetch PLUS the save's returned booking, so they
          // are at least as new as the cache. Keep them; just show the data-age banner.
          if (cached) setOfflineInfo({ refreshedAt: cached.scope.refreshed_at });
          return;
        }
        if (cached) {
          updatedAtRef.current = new Map(cached.bookings.map((b) => [b.id, b.updated_at]));
          setBookings(cached.bookings);
          displayedScopeKeyRef.current = scopeKey; // on-screen data now IS this scope (5d-p3b)
          setBookingsError(null);
          setOfflineInfo({ refreshedAt: cached.scope.refreshed_at });
          setOfflineEmpty(false);
        } else if (!opts.silent) {
          // Never cached on this device → honest empty state (no spinner, no retry box).
          // Silent polls keep whatever is on screen — the connection banner already shows.
          setBookings([]);
          setOfflineInfo(null);
          setOfflineEmpty(true);
        }
        return;
      }
      if (opts.afterSave) flagStaleAfterSave();
      if (!opts.silent) setBookingsError(e instanceof Error ? e.message : 'Failed to load bookings.');
    } finally {
      if (!opts.silent) setBookingsLoading(false);
    }
  }, [refBases, viewMode, date, weekDates]);

  // Debounced refetch: rapid [ref/viewMode/date/weekDates] changes (a user clicking through
  // days/weeks) coalesce into ONE fetch instead of firing + aborting many. Trailing-edge, so
  // the fetch always reflects the FINAL selected state. (HANDOFF S24 — reduces the abort churn
  // that strands DB connections.) The 45 s poll + focus refetch below are already sparse.
  useEffect(() => {
    const t = setTimeout(() => loadBookings(), BOOKINGS_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [loadBookings]);

  // Clear the change-highlight shortly after it appears (brief visual cue).
  useEffect(() => {
    if (changedIds.size === 0) return;
    const t = setTimeout(() => setChangedIds(new Set()), 4000);
    return () => clearTimeout(t);
  }, [changedIds]);

  // Polling (45 s) + refetch on window focus. Skipped while the booking form, a drag, or any
  // overlay interaction (detail panel / modal / resolver) is live — PR-000044/040.
  // 5d-p3a: piggyback the open-conflicts refresh on the SAME cadence (no separate polling loop —
  // scope decision 3). PERF PART C (2026-09-15): these used to be named `notifyRef` / `refreshNotifyState`, but
  // since notifications moved to NotificationsContext it has fetched ONLY /api/conflicts?status=open.
  // Renamed (Ben's ruling) so the name matches the fetch. Notifications' single owner is the context.
  const conflictsRef = useRef(refreshOpenConflicts);
  useEffect(() => { conflictsRef.current = refreshOpenConflicts; }, [refreshOpenConflicts]);
  // 5d Phase 4: a revoked device with NO queued ops never drains, so poll the device's
  // revocation status on the same cadence → purge when revoked (drain handles the
  // has-queued-ops case via its purge flag, which honours the one grace drain first).
  const purgeRef = useRef(purgeAndUnenroll);
  useEffect(() => { purgeRef.current = purgeAndUnenroll; }, [purgeAndUnenroll]);
  const checkRevocation = useCallback(async () => {
    const st = await fetchDeviceStatus(getDeviceId());
    if (!st?.purge || !user) return;
    // Honour drain-once-then-purge: if ops are still queued, run the ONE grace drain first
    // (the server applies them + latches the grace), THEN purge. Empty queue → purge now.
    const queued = await listQueuedOps(user.id);
    if (queued.length > 0) await drainOpQueue(user.id);
    void purgeRef.current();
  }, [user]);
  useEffect(() => {
    const tick = () => { if (!showFormRef.current && !dragActiveRef.current && !overlayOpenRef.current) { loadBookings({ silent: true }); void refreshAircraftSlice(); void conflictsRef.current(); void checkRevocation(); runRollingPrefetch(false); } };
    void conflictsRef.current();
    const id = window.setInterval(tick, POLL_MS);
    window.addEventListener('focus', tick);
    return () => { window.clearInterval(id); window.removeEventListener('focus', tick); abortRef.current?.abort(); };
  }, [loadBookings, checkRevocation, refreshAircraftSlice]);

  // ── 5d-p2: offline op queue (provisional overlay + drain) ────────────────────
  const [queuedOps, setQueuedOps] = useState<QueuedOp[]>([]);
  useEffect(() => {
    if (!user) return;
    const refresh = () => { void listQueuedOps(user.id).then(setQueuedOps); };
    refresh();
    return subscribeOpQueue(refresh);
  }, [user]);

  // Drain: a SUCCESSFUL fetch is the connectivity signal (the connection store flips
  // offline→online on it), plus one attempt on mount for queues left from a previous
  // session. Acked outcomes leave the queue; results surface via the warnings banner
  // (full notification UX is Phase 3); affected scopes refresh after a non-empty drain.
  const drainNow = useCallback(async () => {
    if (!user) return;
    const r = await drainOpQueue(user.id);
    // Revocation: the grace drain (above) applied any queued ops; now purge.
    if (r.purge) { void purgeAndUnenroll(); return; }
    if (r.drained === 0) return;
    const msgs: string[] = [];
    if (r.confirmed > 0) msgs.push(`${r.confirmed} offline change${r.confirmed === 1 ? '' : 's'} synced.`);
    if (r.conflicted > 0) msgs.push(`${r.conflicted} offline change${r.conflicted === 1 ? '' : 's'} hit a conflict at sync — recorded for ops resolution.`);
    for (const rej of r.rejected) msgs.push(`An offline change was rejected: ${rej.reason}`);
    setWarnings(msgs);
    void loadRefData();
    loadBookings({ silent: true });
    void refreshOpenConflicts(); // a drain that hit a conflict just emitted notices
  }, [user, loadRefData, loadBookings, refreshOpenConflicts]);
  const drainRef = useRef(drainNow);
  useEffect(() => { drainRef.current = drainNow; }, [drainNow]);
  useEffect(() => {
    void drainRef.current();
    // 5d-p5: a reconnect is the moment to refresh the rolling cache (force past the freshness
    // gate) so the pilot's offline coverage is current right after regaining signal.
    return subscribeConnection((online) => { if (online) { void drainRef.current(); runRollingPrefetch(true); } });
  }, [user, runRollingPrefetch]);

  // Render-only provisional overlay: queued ops merged onto the fetched bookings.
  const displayBookings = useMemo(
    () => overlayQueuedOps(bookings, queuedOps, ref?.bases ?? []),
    [bookings, queuedOps, ref],
  );

  // open conflict per task_id → drives the board flag + BookingDetail resolve affordance.
  const conflictByTask = useMemo(() => {
    const m = new Map<string, string>(); // task_id → conflict_item_id
    for (const c of openConflicts) if (!m.has(c.task_id)) m.set(c.task_id, c.id);
    return m;
  }, [openConflicts]);
  // task_ids the current user "owns" (created or participates in) — scopes the own-booking interrupt.
  const ownTaskIds = useMemo(() => {
    const s = new Set<string>();
    if (!user) return s;
    for (const b of bookings) {
      if (b.pilot_id === user.id || b.participants.some((p) => p.user_id === user.id)) s.add(b.id);
    }
    return s;
  }, [bookings, user]);

  // ── Modals / panels ───────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [showCachePanel, setShowCachePanel] = useState(false); // 5d-p5 offline cache inventory/picker
  const [editing, setEditing] = useState<Booking | null>(null);
  const [formPrefill, setFormPrefill] = useState<{ baseId?: string; aircraftReg?: string; assetId?: string; pilotId?: string; startTime?: string; date?: string } | undefined>();
  const [formSeed, setFormSeed] = useState<BookingFormSeed | undefined>(); // recurring/copy Phase 1
  const [seriesAction, setSeriesAction] = useState<{ booking: Booking; action: SeriesAction } | null>(null); // recurring Phase 3
  const [selected, setSelected] = useState<Booking | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [showOutside, setShowOutside] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  // Mobile-only "Overview" drawer (Option B) — tabbed This Day / Staff / Aircraft views over the
  // already-loaded data. Replaces the inline right panel, which is hidden on phones.
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [overviewTab, setOverviewTab] = useState<'day' | 'staff' | 'aircraft'>('day');
  const [relocateModal, setRelocateModal] = useState<{ reg: string; suggestIcao?: string } | null>(null);
  const [relocatePrompt, setRelocatePrompt] = useState<{ reg: string; baseName: string; baseIcao: string } | null>(null);

  useEffect(() => { showFormRef.current = showForm; }, [showForm]);
  // PR-000044/040: overlay mirror for the poll guard (see overlayOpenRef declaration).
  useEffect(() => {
    overlayOpenRef.current = !!(selected || relocateModal || relocatePrompt || seriesAction || resolverConflictId);
  }, [selected, relocateModal, relocatePrompt, seriesAction, resolverConflictId]);
  // 0.12.11 addendum §1: a write answered 503 DB_UNAVAILABLE may or may not have committed, so the
  // board refreshes itself in the background (the post-save silent refresh: aircraft slice + one
  // silent bookings load) and the truth appears as soon as the database answers. Under the SAME guard
  // as the poll: while a form, a drag or an overlay is open the refresh is deferred until it closes,
  // so it can never clobber what the user typed (the form keeps its contents on this error).
  useEffect(() => {
    dbRefreshRef.current = () => { dbRefreshPendingRef.current = false; loadBookings({ silent: true }); void refreshAircraftSlice(); };
  }, [loadBookings, refreshAircraftSlice]);
  useEffect(() => {
    const onDbUnavailableWrite = () => {
      if (!showFormRef.current && !dragActiveRef.current && !overlayOpenRef.current) dbRefreshRef.current();
      else dbRefreshPendingRef.current = true;
    };
    window.addEventListener(DB_UNAVAILABLE_WRITE_EVENT, onDbUnavailableWrite);
    return () => window.removeEventListener(DB_UNAVAILABLE_WRITE_EVENT, onDbUnavailableWrite);
  }, []);
  useEffect(() => {
    if (dbRefreshPendingRef.current && !showForm && !(selected || relocateModal || relocatePrompt || seriesAction || resolverConflictId)) dbRefreshRef.current();
  }, [showForm, selected, relocateModal, relocatePrompt, seriesAction, resolverConflictId]);
  // Keep the open detail panel in sync with refreshed data (e.g. after a poll/allocate).
  useEffect(() => {
    if (!selected) return;
    const fresh = displayBookings.find((b) => b.id === selected.id);
    if (fresh && (fresh.updated_at !== selected.updated_at || fresh._pending_op !== selected._pending_op)) setSelected(fresh);
  }, [displayBookings, selected]);

  const lookups = useMemo(() => ({
    bases: new Map((ref?.bases ?? []).map((b) => [b.id, b])),
    aircraft: new Map((ref?.aircraft ?? []).map((a) => [a.registration, a])),
    assets: new Map((ref?.assets ?? []).map((a) => [a.id, a])),
    staff: new Map((ref?.staff ?? []).map((s) => [s.id, s])),
    students: new Map((ref?.students ?? []).map((s) => [s.id, s])),
    activities: new Map((ref?.activities ?? []).map((a) => [a.id, a])),
    bookingKinds: new Map((ref?.bookingKinds ?? []).map((k) => [k.id, k])),
  }), [ref]);

  // All derived sets read displayBookings (fetched + provisional overlay, 5d-p2).
  const liveBookings = useMemo(() => displayBookings.filter((b) => b.booking_status !== 'cancelled'), [displayBookings]);
  // Bookings that OCCUPY a resource line (non-cancelled, not parked on the waitlist).
  const occupyingBookings = useMemo(() => liveBookings.filter((b) => !b.waitlisted), [liveBookings]);
  // Waitlisted bookings (non-cancelled) — rendered on the dedicated waitlist line.
  const waitlistBookings = useMemo(() => liveBookings.filter((b) => b.waitlisted), [liveBookings]);
  // Recurring Phase 2 — confirmed series occurrences still UNASSIGNED (no crew + no allocation).
  // They occupy no resource row, so they get their own "needs allocation" lane (distinct from the
  // waitlist). Assign on the day = open one → add crew + aircraft → Save (a normal PUT).
  const unassignedRecurring = useMemo(
    () => liveBookings.filter((b) => b.series_id != null && !b.waitlisted && b.booking_status === 'confirmed' && b.participants.length === 0 && b.asset_allocations.length === 0),
    [liveBookings],
  );
  // Active (un-returned) maintenance bookings — let DayView resolve the post-ECD tail (which falls
  // outside the day's pool) to its booking for a clickable confirm-return. From the WIDER fetched
  // set (liveBookings), so the immediate tail resolves even though dayPool excludes it.
  const activeMaint = useMemo(() => {
    const maintKindIds = new Set((ref?.bookingKinds ?? []).filter((k) => k.is_maintenance).map((k) => k.id));
    return liveBookings.filter((b) => b.booking_kind_id && maintKindIds.has(b.booking_kind_id) && !b.maintenance_returned_at);
  }, [liveBookings, ref]);

  const overlapsDay = useCallback((b: Booking): boolean => {
    if (!b.block_start_at) return false;
    const { from, to } = zonedDayRangeUtc(date, b.dep_timezone);
    const s = new Date(b.block_start_at).getTime();
    return bookingEndMs(b) > new Date(from).getTime() && s < new Date(to).getTime();
  }, [date]);

  // Week-level analogue of overlapsDay — true when a booking overlaps the viewed week
  // [weekDates[0], weekDates[6]] (same bookingEndMs / multi-tz basis). Needed for week-mode
  // stats: since the far-overdue maintenance carve-out (branch 2 of listBookings) has NO date
  // bound, occupyingBookings now carries active maintenance from OUTSIDE the viewed week
  // (including FUTURE scheduled maintenance), which would inflate the stats bar if counted raw.
  // WeekView row placement is unaffected (it bounds each row to the week itself).
  const overlapsWeek = useCallback((b: Booking): boolean => {
    if (!b.block_start_at) return false;
    const from = zonedDayRangeUtc(weekDates[0], b.dep_timezone).from;
    const to = zonedDayRangeUtc(weekDates[6], b.dep_timezone).to;
    const s = new Date(b.block_start_at).getTime();
    return bookingEndMs(b) > new Date(from).getTime() && s < new Date(to).getTime();
  }, [weekDates]);

  // Day view pool: bookings overlapping the selected day (multi-day / multi-tz aware),
  // including cancelled ones when the "Show cancelled" toggle is on (DayView styles
  // them) and waitlisted ones (DayView renders them only on the waitlist line).
  const dayPool = useMemo(
    () => (showCancelled ? displayBookings : liveBookings).filter(overlapsDay),
    [showCancelled, displayBookings, liveBookings, overlapsDay],
  );
  // Occupying bookings on the selected day — for stats + the (non-waitlist) resource rows.
  const dayBookings = useMemo(() => occupyingBookings.filter(overlapsDay), [occupyingBookings, overlapsDay]);
  const dayWaitlist = useMemo(() => waitlistBookings.filter(overlapsDay), [waitlistBookings, overlapsDay]);
  const dayUnassigned = useMemo(() => unassignedRecurring.filter(overlapsDay), [unassignedRecurring, overlapsDay]);

  const sectionBases = useMemo(
    () => (allBases ? (ref?.bases ?? []) : selectedBase ? [selectedBase] : []),
    [allBases, ref, selectedBase],
  );

  // Human-readable scope for the offline banner / empty state ("[scope]" in v2 §8).
  const offlineScopeLabel = useMemo(() => {
    const baseLabel = allBases ? 'all-bases' : selectedBase?.name ?? 'board';
    const dateLabel = viewMode === 'day'
      ? `${weekdayLabel(date)} ${dayMonthLabel(date)}`
      : `week of ${dayMonthLabel(weekDates[0])}`;
    return `${baseLabel} · ${dateLabel}`;
  }, [allBases, selectedBase, viewMode, date, weekDates]);

  // Stats scoped to what's visible (selected base in single-base mode).
  const statBookings = useMemo(() => {
    const set = viewMode === 'day' ? dayBookings : occupyingBookings.filter(overlapsWeek);
    return allBases ? set : set.filter((b) => b.base_id === selectedBase?.id);
  }, [viewMode, dayBookings, occupyingBookings, overlapsWeek, allBases, selectedBase]);

  const outsideBookings = useMemo(() => statBookings.filter((b) => b.outside_hours), [statBookings]);

  // ⚠ THE NOTIFICATION COUNT NEEDS THE WAITLIST TOO (Ben, 2026-09-18), and that is NOT the same set
  // as `statBookings`. statBookings comes from `occupyingBookings`, which is `liveBookings.filter(b
  // => !b.waitlisted)` — waitlisted bookings are deliberately a separate lane. So when the rule
  // changed to "a displaced booking counts as needing a call", changing notifyWarning() alone would
  // have fixed nothing here: the rows carrying the pen were never in the set being counted.
  //
  // Scoped EXACTLY like statBookings (same day/week filter, same base filter) so the count matches
  // what is on screen. Deliberately its own memo rather than widening statBookings: `total`,
  // `confirmed` and `pending` are counts of bookings that OCCUPY a resource line, and a displaced
  // booking must not start inflating them.
  const notifyBookings = useMemo(() => {
    const wl = viewMode === 'day' ? dayWaitlist : waitlistBookings.filter(overlapsWeek);
    const scoped = allBases ? wl : wl.filter((b) => b.base_id === selectedBase?.id);
    return [...statBookings, ...scoped];
  }, [viewMode, dayWaitlist, waitlistBookings, overlapsWeek, allBases, selectedBase, statBookings]);

  // Waitlist scoped to the visible set (day view + base scope) — drives the alert chip.
  const visibleWaitlist = useMemo(() => {
    const set = viewMode === 'day' ? dayWaitlist : waitlistBookings;
    return allBases ? set : set.filter((b) => b.base_id === selectedBase?.id);
  }, [viewMode, dayWaitlist, waitlistBookings, allBases, selectedBase]);

  const stats = useMemo(() => ({
    total: statBookings.length,
    confirmed: statBookings.filter((b) => b.approval_status === 'not_required' || b.approval_status === 'approved').length,
    pending: statBookings.filter((b) => b.approval_status.startsWith('pending')).length,
    // ⚠ WAS `notified: …filter(b => b.student_notified)`, THE SAME CLAIM THE CHIP AND THE PANEL
    // STOPPED MAKING (Ben, 2026-09-17). student_notified records an APPROVAL OUTCOME, not that a
    // human told anybody, so a "Student notified" count on the board was a reassurance nothing
    // supported. This counts the OTHER direction instead: the bookings on the viewed day that
    // somebody still has to ring, through the SAME notifyWarning() the chip badge and the detail
    // panel read, so the number on the panel and the badges on the board can never disagree.
    // ⚠ COUNTED OVER notifyBookings, NOT statBookings (Ben, 2026-09-18): a maintenance-displaced
    // booking is waitlisted, and waitlisted rows are not in statBookings at all. notifyWarning()
    // still excludes cancelled bookings, and a waitlisted booking with no pen, itself.
    outstandingNotifications: notifyBookings.filter((b) => notifyWarning(b) !== null).length,
    outside: outsideBookings.length,
  }), [statBookings, notifyBookings, outsideBookings]);
  // Single source for the THIS DAY stat rows — reused by the desktop panel AND the mobile drawer.
  const statRows = [
    ['Total bookings', stats.total, C.text],
    ['Confirmed/approved', stats.confirmed, C.green],
    ['Pending approval', stats.pending, C.amber],
    ['Outstanding notifications', stats.outstandingNotifications, C.amber],
    ['Outside hours', stats.outside, C.amber],
  ] as const;

  const scopeIcaos = useMemo<Set<string> | null>(
    () => (allBases ? null : selectedBase?.icao ? new Set([selectedBase.icao]) : new Set<string>()),
    [allBases, selectedBase],
  );

  function openNew() {
    setEditing(null);
    setFormSeed(undefined);
    // Task B1 — base default for a blank "New Booking" = the user's nominated default base for
    // everyone (no role logic). A valid home base pre-fills; otherwise the field starts blank and
    // the form's "— select base —" + save-block require a choice. (Slot/asset click still wins — openSlot.)
    const homeOk = !!user?.home_base_id && !!ref?.bases.some((b) => b.id === user.home_base_id);
    const baseId = homeOk ? user!.home_base_id! : '';
    setFormPrefill({ baseId, date });
    setShowForm(true);
  }
  function openSlot(slot: { baseId: string; aircraftReg?: string; assetId?: string; pilotId?: string; startTime: string }) {
    setEditing(null);
    setFormSeed(undefined);
    setFormPrefill({ baseId: slot.baseId || formBaseId, aircraftReg: slot.aircraftReg, assetId: slot.assetId, pilotId: slot.pilotId, startTime: slot.startTime, date });
    setShowForm(true);
  }
  function openEdit(b: Booking) { setSelected(null); setEditing(b); setFormPrefill(undefined); setFormSeed(undefined); setShowForm(true); }

  // Copy a booking (recurring/copy Phase 1) — open a prefilled NEW booking from an existing one.
  // GET /api/bookings/:id for the FRESHEST full source; on any failure fall back to the already
  // -loaded row. buildCopyDraft maps it onto a create-mode seed; the source is never mutated.
  async function openCopy(b: Booking) {
    setSelected(null);
    let source: Booking = b;
    try {
      const res = await apiFetch<Booking>(`/api/bookings/${b.id}`);
      if (res.data) source = res.data;
      else if (res.error) setWarnings([`Couldn't refresh the booking to copy — copying from the loaded view. (${res.error.message})`]);
    } catch {
      // Offline / network error: fall back to the in-memory row (the create path still works).
      setWarnings(['Offline — copying from the loaded view.']);
    }
    setEditing(null);
    setFormPrefill(undefined);
    setFormSeed(buildCopyDraft(source));
    setShowForm(true);
  }

  async function onFormSubmitted(w: string[], booking: Booking | null) {
    setShowForm(false);
    setEditing(null);
    setFormSeed(undefined);
    setWarnings(w);
    // A maintenance create/edit flips aircraft serviceable→false + sets the ECD on the *aircraft*
    // record; the board's maintenance shading is derived from that aircraft ref data, NOT from
    // bookings, so the aircraft slice must refresh after a save (refreshing bookings alone left the
    // shading stale until a manual refresh).
    // 0.12.10 Part B: ONLY the aircraft slice. The aircraft row is the only reference data a booking
    // write changes (every tx.aircraft.update in bookings.service.ts; no other reference table is
    // written), so the full nine-endpoint sweep that ran here is gone. Show the saved booking from
    // the save's own response at once (as doReschedule does), then ONE silent refetch, which is still
    // needed because a write can move other rows too.
    if (booking) setBookings((prev) => prev.map((x) => (x.id === booking.id ? booking : x)));
    await refreshAircraftSlice();
    loadBookings({ silent: true, afterSave: true });
    // Relocation prompt: an aircraft booked away from its current base (ops/admin only).
    if (canWorkflow && booking && ref) {
      const base = ref.bases.find((b) => b.id === booking.base_id);
      const reg = booking.asset_allocations.find((a) => a.aircraft_registration)?.aircraft_registration ?? null;
      const ac = reg ? ref.aircraft.find((a) => a.registration === reg) : null;
      if (ac && base?.icao && ac.current_base_icao && ac.current_base_icao !== base.icao) {
        setRelocatePrompt({ reg: ac.registration, baseName: base.name, baseIcao: base.icao });
      }
    }
  }
  async function onWorkflowChanged(opts?: { reachedServer: boolean }) {
    setSelected(null);
    // Confirm-returned (and approve/decline/cancel) can restore aircraft serviceability
    // + clear the ECD on the aircraft record, which drives the maintenance shading — so
    // refresh ref data too, not just bookings, or the shading won't clear immediately.
    // 0.12.10 Part B: the aircraft slice only (see onFormSubmitted), then ONE silent refetch.
    // 0.12.10 addendum 2: `afterSave` (the "Saved." line and keep-on-screen) ONLY when the write
    // reached the server. An offline-QUEUED action is not saved; its refetch is an ordinary silent one.
    await refreshAircraftSlice();
    loadBookings({ silent: true, afterSave: opts?.reachedServer !== false });
  }

  // PR-000031 — commit an edge-drag reschedule. One normalized write for every drag: explicit
  // block_start_at + arr_time_planned with activity_mins recomputed to match (design §3), through
  // the SAME PUT the form uses — §B hard-block + buffer/serviceability checks all re-run server-
  // side. A rejection surfaces the existing plain-English reason and resolves ok:false (DayView
  // snaps the chip back). Offline (network-level failure only) queues the standard booking_edit
  // op — the provisional overlay then renders the moved chip until the drain syncs it.
  async function doReschedule(b: Booking, patch: { block_start_at: string; arr_time_planned: string; activity_mins: number }): Promise<{ ok: boolean }> {
    try {
      const res = await apiFetch<Booking>(`/api/bookings/${b.id}`, { method: 'PUT', body: JSON.stringify(patch) });
      if (res.error) {
        // 0.12.11: DB_UNAVAILABLE carries its own complete sentence ("Couldn't confirm your change was
        // saved…"); a "Couldn't reschedule" prefix would contradict it. The chip snaps back and the
        // background refresh shows where the booking really is.
        setWarnings([res.error.code === DB_UNAVAILABLE ? res.error.message : `Couldn't reschedule — ${res.error.message}`]);
        return { ok: false };
      }
      const updated = res.data;
      // Show the move immediately (no spinner), then confirm with a silent refetch.
      if (updated) setBookings((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      setWarnings(res.warnings ?? []);
      loadBookings({ silent: true, afterSave: true });
      return { ok: true };
    } catch (e) {
      if (e instanceof NetworkError && user) {
        const readStamp = getBookingsReadStamp();
        const stale = stalenessFor('booking_edit', readStamp);
        const op = await enqueueOp(user.id, 'booking_edit', { ...patch, task_id: b.id }, readStamp, stale.record);
        if (op) {
          const msgs = ['Offline — reschedule saved to this device. It will sync automatically when reconnected; availability can\'t be confirmed until then.'];
          if (stale.message) msgs.unshift(stale.message);
          setWarnings(msgs);
          return { ok: true }; // the provisional overlay carries the moved chip
        }
        setWarnings(['Offline, and the on-device queue is unavailable — this change could not be saved.']);
        return { ok: false };
      }
      setWarnings([e instanceof Error ? e.message : 'Reschedule failed.']);
      return { ok: false };
    }
  }

  async function doRelocate(reg: string, icao: string) {
    try {
      const res = await apiFetch<unknown>(`/api/aircraft/${encodeURIComponent(reg)}/relocate`, { method: 'POST', body: JSON.stringify({ current_base_icao: icao }) });
      if (res.error) { setWarnings([res.error.code === DB_UNAVAILABLE ? res.error.message : `Relocate failed: ${res.error.message}`]); return; }
      setWarnings(res.warnings ?? []);
      await loadRefData();
      loadBookings();
    } catch (e) {
      setWarnings([e instanceof Error ? e.message : 'Relocate failed.']);
    }
  }

  function toggleFilter(k: keyof RowFilters) { setFilters((f) => ({ ...f, [k]: !f[k] })); }

  // Mobile drawer → tap a Staff/Aircraft row to close the sheet and scroll that resource's board
  // row into view. The ids are stamped on the resident rows in DayView (a non-behavioral attr).
  function focusResource(id: string) {
    setOverviewOpen(false);
    window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  // Only take over the whole screen on the FIRST load (no data yet). A background refresh — e.g.
  // loadRefData() fired by onPeopleChanged after an inline OR ☰-menu student/instructor create —
  // must NOT unmount the board, or the open BookingForm remounts blank and loses the operator's
  // progress. `ref` is null until the first successful load; the `if (!ref) return null;` below
  // still guards that case.
  if (refLoading && !ref) return <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner label="Loading Ops Board…" /></div>;
  if (refError) return <div style={{ minHeight: '100vh', background: C.bg, padding: 40 }}><ErrorBox message={refError} onRetry={loadRefData} /></div>;
  // Offline with nothing cached on this device (5d Ph.1): honest empty state, not a spinner.
  if (refNoCache) return <div style={{ minHeight: '100vh', background: C.bg, padding: 40 }}><OfflineEmptyState scopeLabel="The ops board" /></div>;
  if (!ref) return null;

  const dayProps = {
    allBases, date, aircraft: ref.aircraft, assets: ref.assets, staff: ref.staff, bases: ref.bases,
    bookingKinds: ref.bookingKinds, activities: ref.activities, activeMaint, filters, canWorkflow, showCancelled, changedIds, waitlist: dayWaitlist, unassigned: dayUnassigned,
    canBook,
    // 0.12.4 — the tenant's chip colour channels. Resolved HERE, once, and handed down already
    // translated: 'booking_kind' (the settings spelling) becomes 'kind' (the render spelling)
    // inside resolveChipChannels and nowhere else. It also holds the two rules the database does
    // not: an unrecognised value and two EQUAL channels both fall back to CHIP_CHANNELS rather
    // than rendering. ⚠ ref.settings is nullable — resolveChipChannels takes null.
    chipChannels: resolveChipChannels(ref.settings),
    onBookingClick: setSelected, onSlotClick: openSlot, onRelocate: (reg: string) => setRelocateModal({ reg }),
    onReschedule: doReschedule, onDragActiveChange,
  };
  const weekProps = {
    allBases, aircraft: ref.aircraft, assets: ref.assets, staff: ref.staff, bases: ref.bases,
    weekDates, today: todayInTz(anchorTz), filters, onDayClick: (d: string) => { setDate(d); setViewMode('day'); },
  };

  const renderSections = (vm: ViewMode) =>
    vm === 'day' ? (
      <>
        {sectionBases.map((b) => <DayView key={b.id} base={b} showHeader={allBases} bookings={dayPool} {...dayProps} />)}
        {filters.aircraft && <DayView base={null} showHeader bookings={dayPool} {...dayProps} />}
        {/* The legend reads the SAME resolved channels the chips do, so it cannot go on
            describing a scheme the board has stopped using (0.12.4 Part E). */}
        <DayViewLegend chipChannels={dayProps.chipChannels} />
      </>
    ) : (
      <>
        {sectionBases.map((b) => <WeekView key={b.id} base={b} showHeader={allBases} bookings={occupyingBookings} {...weekProps} />)}
        {filters.aircraft && <WeekView base={null} showHeader bookings={occupyingBookings} {...weekProps} />}
        <div style={{ marginTop: 10, fontSize: 10, color: C.textMuted, fontFamily: F }}>Click any cell to open that day in Day view.</div>
      </>
    );

  return (
    // 100dvh flex column: AppHeader + the board toolbar are fixed-height (flexShrink:0); the BODY is
    // flex:1 and its board column scrolls internally, so the header + toolbar stay pinned and the
    // time-axis (sticky within the board column) pins beneath them. dvh (not vh) so the pinned
    // chrome survives the iOS dynamic toolbar. The header wraps on narrow widths — a flex column
    // absorbs that with no hardcoded height math.
    <div className="screen-h" style={{ background: C.bg, fontFamily: F, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Corporate brand strip (AvSuite top-left / Flight Standards top-right). The board's
          own dense controls below are left in place — see HANDOFF re: centred Row-2 deferral. */}
      {/* onPeopleChanged: a ☰ "New student / client" create refreshes the people reference so the
          new person is immediately bookable. loadRefData is a stable useCallback and, with the
          `refLoading && !ref` guard above, the background refresh never unmounts the open form. */}
      {/* Mobile (2026-07-23 rework): the Beta (admin) + Offline toolbar buttons park in the ☰
          menu so the primary toolbar stays one-glance. Desktop keeps them as toolbar buttons. */}
      <AppHeader
        onPeopleChanged={loadRefData}
        menuExtras={isMobile ? [
          ...(isAdmin ? [{ key: 'beta', label: '📊 Beta instrumentation', onSelect: () => navigate('/beta') }] : []),
          { key: 'offline', label: '📥 Offline cache', onSelect: () => setShowCachePanel(true) },
        ] : undefined}
      />
      {showForm && (
        <BookingForm
          bases={ref.bases} aircraft={ref.aircraft} assets={ref.assets} staff={ref.staff} students={ref.students}
          activities={ref.activities} bookingKinds={ref.bookingKinds} settings={ref.settings} defaultBaseId={formBaseId} prefill={formPrefill}
          seed={formSeed}
          editing={editing} onClose={() => { setShowForm(false); setEditing(null); setFormSeed(undefined); }} onSubmitted={onFormSubmitted} onPeopleChanged={loadRefData}
        />
      )}

      {showCachePanel && (
        <OfflineCachePanel
          bases={ref.bases}
          homeWindow={homeWindow}
          homeWindowKey={homeWindow ? bookingsScopeKey(null, homeWindow.from, homeWindow.to) : null}
          anchorTz={anchorTz}
          onClose={() => setShowCachePanel(false)}
        />
      )}

      {relocateModal && (
        <RelocateModal
          registration={relocateModal.reg}
          aircraft={ref.aircraft.find((a) => a.registration === relocateModal.reg)}
          bases={ref.bases}
          suggestIcao={relocateModal.suggestIcao}
          onClose={() => setRelocateModal(null)}
          onDone={(w) => { setRelocateModal(null); setWarnings(w); loadRefData(); loadBookings(); }}
        />
      )}

      {relocatePrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 320, padding: 16 }} onClick={() => setRelocatePrompt(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, width: '100%', maxWidth: 380, fontFamily: F }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>Relocate {relocatePrompt.reg}?</div>
            <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.5, marginBottom: 16 }}>
              {relocatePrompt.reg} is based elsewhere — move it to <strong>{relocatePrompt.baseName}</strong>'s listing, or keep it as a visiting aircraft?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setRelocatePrompt(null)} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '9px', fontSize: 12, cursor: 'pointer' }}>Keep as visiting</button>
              <button onClick={() => { const p = relocatePrompt; setRelocatePrompt(null); doRelocate(p.reg, p.baseIcao); }} style={{ flex: 2, background: C.navy, border: 'none', color: '#fff', borderRadius: 6, padding: '9px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Relocate to {relocatePrompt.baseName}</button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER — mobile (≤768px, 2026-07-23 rework, Ben-approved mockup with the title folded
          in): NO title block — page identity is carried by the base pill + context. Primary
          controls only (Bases single-pick, Show checklist, Day/Week, date stepper, + New,
          Refresh icon); Beta/Offline live in the ☰ menu; the status chips wrap in their own
          slim row; updated-time + base hours become a one-line caption.
          Desktop (≥769px): the original dense block, untouched. */}
      {isMobile ? (
        <div style={{ background: C.navy, padding: '8px 10px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', flexShrink: 0 }}>
          {/* Row 1 — Bases · Show · Day/Week · + New */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <MobileBaseSelect
              bases={ref.bases}
              baseId={baseId}
              onPick={(id) => {
                if (id === ALL_BASES) { setBaseId(ALL_BASES); return; }
                const b = ref.bases.find((x) => x.id === id);
                setBaseId(id);
                if (b) setDate((d) => d || todayInTz(b.timezone)); // same per-base default as the chips
              }}
            />
            <MobileShowSelect
              filters={filters} onToggleFilter={toggleFilter}
              showCancelled={showCancelled} onToggleCancelled={() => setShowCancelled((v) => !v)}
              canWorkflow={canWorkflow}
            />
            <div style={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 7, overflow: 'hidden' }}>
              {(['day', 'week'] as ViewMode[]).map((m) => (
                <button key={m} onClick={() => setViewMode(m)} style={{ background: viewMode === m ? 'rgba(255,255,255,0.22)' : 'transparent', border: 'none', color: viewMode === m ? '#fff' : 'rgba(255,255,255,0.55)', padding: '9px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>{m === 'day' ? 'Day' : 'Week'}</button>
              ))}
            </div>
            <button onClick={openNew} style={{ marginLeft: 'auto', background: C.accent, border: 'none', color: '#fff', borderRadius: 7, padding: '9px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F, whiteSpace: 'nowrap' }}>+ New</button>
          </div>

          {/* Row 2 — date stepper (weekday + native date input as the pick target) · Today · ⟳ */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 7, overflow: 'hidden' }}>
              <button
                onClick={() => setDate((d) => addDays(d, viewMode === 'week' ? -7 : -1))}
                aria-label={viewMode === 'week' ? 'Previous week' : 'Previous day'}
                title={viewMode === 'week' ? 'Previous week' : 'Previous day'}
                style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#fff', padding: '9px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: F }}
              >‹</button>
              <span style={{ padding: '0 4px 0 8px', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', fontFamily: F }}>{weekdayLabel(date)}</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Board date" style={{ background: 'transparent', border: 'none', color: '#fff', padding: '7px 4px', fontSize: 12, fontFamily: F, colorScheme: 'dark' }} />
              <button
                onClick={() => setDate((d) => addDays(d, viewMode === 'week' ? 7 : 1))}
                aria-label={viewMode === 'week' ? 'Next week' : 'Next day'}
                title={viewMode === 'week' ? 'Next week' : 'Next day'}
                style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#fff', padding: '9px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: F }}
              >›</button>
            </div>
            <button onClick={() => setDate(todayInTz(anchorTz))} style={{ ...mobilePill, padding: '9px 11px' }}>Today</button>
            {/* PR-000044 refresh, icon-only on mobile: 44×44 hit area drawing a 34×34 box. */}
            <button
              onClick={() => { loadBookings(); void refreshAircraftSlice(); void refreshOpenConflicts(); }}
              aria-label="Refresh the board now" title="Refresh the board now"
              style={{ width: 44, height: 44, padding: 0, background: 'transparent', border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: 'auto' }}
            >
              <span aria-hidden="true" style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: '#fff', fontSize: 15, lineHeight: 1 }}>⟳</span>
            </button>
          </div>

          {/* Status chips — own slim wrap row so they never push the primary controls off-screen. */}
          {(stats.pending > 0 || visibleWaitlist.length > 0 || stats.outside > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {stats.pending > 0 && <div style={{ background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 5, padding: '4px 10px', fontSize: 10, color: C.amber, fontWeight: 700 }}>⏳ {stats.pending} pending approval</div>}
              {visibleWaitlist.length > 0 && (
                <button onClick={() => setShowWaitlist((v) => !v)} style={{ background: C.amber, border: '1px solid ' + C.amberBorder, borderRadius: 5, padding: '4px 10px', fontSize: 10, color: '#fff', cursor: 'pointer', fontFamily: F, fontWeight: 700 }}>⏳ {visibleWaitlist.length} unallocated ▾</button>
              )}
              {stats.outside > 0 && (
                <button onClick={() => setShowOutside((v) => !v)} style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 5, padding: '4px 10px', fontSize: 10, color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontFamily: F, fontWeight: 700 }}>⏰ {stats.outside} outside hours ▾</button>
              )}
            </div>
          )}

          {/* Caption — base hours + updated time (was its own toolbar row on desktop). */}
          <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.55)', fontFamily: F }}>
            {selectedBase
              ? `🕐 ${selectedBase.name} ${selectedBase.operating_hours_start ?? ''} to ${selectedBase.operating_hours_end ?? ''} (${selectedBase.timezone})`
              : '🌐 All bases'}
            {lastUpdated && ` · updated ${new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(lastUpdated)}`}
          </div>
        </div>
      ) : (
      <div style={{ background: C.navy, padding: '12px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✈</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>FLIGHT SCHOOL OPS</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em' }}>{ref.bases.map((b) => b.name.toUpperCase()).join(' · ')}</div>
            </div>
            <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.15)' }} />
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>{weekdayLabel(date).toUpperCase()} {dayMonthLabel(date)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {stats.pending > 0 && <div style={{ background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 5, padding: '4px 10px', fontSize: 10, color: C.amber, fontWeight: 700 }}>⏳ {stats.pending} pending approval</div>}
            {visibleWaitlist.length > 0 && (
              <button onClick={() => setShowWaitlist((v) => !v)} style={{ background: C.amber, border: '1px solid ' + C.amberBorder, borderRadius: 5, padding: '4px 10px', fontSize: 10, color: '#fff', cursor: 'pointer', fontFamily: F, fontWeight: 700 }}>⏳ {visibleWaitlist.length} unallocated ▾</button>
            )}
            {stats.outside > 0 && (
              <button onClick={() => setShowOutside((v) => !v)} style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 5, padding: '4px 10px', fontSize: 10, color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontFamily: F, fontWeight: 700 }}>⏰ {stats.outside} outside hours ▾</button>
            )}
            <button onClick={openNew} style={{ background: C.accent, border: 'none', color: '#fff', borderRadius: 5, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>+ New Booking</button>
            {isAdmin && (
              <button onClick={() => navigate('/beta')} title="Beta parallel-run instrumentation (admin)" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)', borderRadius: 5, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>📊 Beta</button>
            )}
            <button onClick={() => setShowCachePanel(true)} title="Offline cache — coverage & take ranges offline" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)', borderRadius: 5, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>📥 Offline</button>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 3 }}>
            <button onClick={() => setBaseId(ALL_BASES)} style={{ background: allBases ? C.accent : 'rgba(255,255,255,0.08)', border: `1px solid ${allBases ? C.accent : 'rgba(255,255,255,0.15)'}`, color: allBases ? '#fff' : 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>All bases</button>
            {ref.bases.map((b) => (
              <button key={b.id} onClick={() => { setBaseId(b.id); setDate((d) => d || todayInTz(b.timezone)); }} style={{ background: baseId === b.id ? b.ui_color : 'rgba(255,255,255,0.08)', border: `1px solid ${baseId === b.id ? b.ui_color : 'rgba(255,255,255,0.15)'}`, color: baseId === b.id ? '#fff' : 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>{b.name}</button>
            ))}
          </div>
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
          <div style={{ display: 'flex', gap: 3 }}>
            {(['day', 'week'] as ViewMode[]).map((m) => (
              <button key={m} onClick={() => setViewMode(m)} style={{ background: viewMode === m ? 'rgba(255,255,255,0.2)' : 'transparent', border: `1px solid ${viewMode === m ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)'}`, color: viewMode === m ? '#fff' : 'rgba(255,255,255,0.5)', borderRadius: 5, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>{m === 'day' ? 'Day' : 'Week'} View</button>
            ))}
          </div>
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {(['aircraft', 'instructors', 'assets'] as (keyof RowFilters)[]).map((k) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', textTransform: 'capitalize', fontFamily: F }}>
                <input type="checkbox" checked={filters[k]} onChange={() => toggleFilter(k)} style={{ cursor: 'pointer', accentColor: C.accent }} />{k}
              </label>
            ))}
            {canWorkflow && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontFamily: F }}>
                <input type="checkbox" checked={showCancelled} onChange={() => setShowCancelled((v) => !v)} style={{ cursor: 'pointer', accentColor: C.accent }} />Show cancelled
              </label>
            )}
          </div>
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <button onClick={() => setDate((d) => addDays(d, viewMode === 'week' ? -7 : -1))} style={navBtn}>‹</button>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 5, padding: '3px 8px', fontSize: 11, fontFamily: F, colorScheme: 'dark' }} />
            <button onClick={() => setDate((d) => addDays(d, viewMode === 'week' ? 7 : 1))} style={navBtn}>›</button>
            <button onClick={() => setDate(todayInTz(anchorTz))} style={{ ...navBtn, width: 'auto', padding: '3px 10px' }}>Today</button>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* PR-000044: explicit refresh — the background poll defers while an overlay is
                open, so this gives users a way to pull fresh data on demand. Non-silent is
                deliberate: an explicit click earns the loading feedback. */}
            <button onClick={() => { loadBookings(); void refreshAircraftSlice(); void refreshOpenConflicts(); }}
              title="Refresh the board now"
              style={{ ...navBtn, width: 'auto', padding: '3px 10px' }}>⟳ Refresh</button>
            {lastUpdated && (
              <span title="Auto-refreshes every 45s and on focus. Paused while a booking form or dialog is open." style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', fontFamily: F }}>
                updated {new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(lastUpdated)}
              </span>
            )}
            {selectedBase ? (
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>🕐 {selectedBase.name} {selectedBase.operating_hours_start ?? '—'}–{selectedBase.operating_hours_end ?? '—'} ({selectedBase.timezone})</div>
            ) : (
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>🌐 All bases</div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* BODY — fills the remaining height; the board column scrolls internally (X+Y) so the
          time-axis can stick to the top of that scrollport (not the document). */}
      <div style={{ padding: isMobile ? '12px 10px' : '16px 20px', display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
        <div ref={boardScrollRef} style={{ flex: 1, minWidth: 0, overflow: 'auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          <WarningsBanner warnings={warnings} onDismiss={() => setWarnings([])} />
          {offlineInfo && <OfflineDataBanner scopeLabel={offlineScopeLabel} refreshedAt={offlineInfo.refreshedAt} />}
          {bookingsError && <ErrorBox message={bookingsError} onRetry={loadBookings} />}
          {bookingsLoading && <Spinner label="Loading bookings…" />}
          {!bookingsLoading && !bookingsError && (
            offlineEmpty ? <OfflineEmptyState scopeLabel={offlineScopeLabel} /> : renderSections(viewMode)
          )}
        </div>

        {/* RIGHT PANEL — desktop only; on mobile it moves into the Overview drawer below. Scrolls
            on its own if taller than the viewport now that the page no longer document-scrolls. */}
        <div style={{ width: 224, flexShrink: 0, display: isMobile ? 'none' : 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, letterSpacing: '0.08em', marginBottom: 10 }}>{viewMode === 'day' ? 'THIS DAY' : 'THIS WEEK'}{allBases ? ' · ALL BASES' : ''}</div>
            {statRows.map(([l, v, c]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: C.textMuted }}>{l}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: c, fontFamily: F }}>{v}</span>
              </div>
            ))}
          </div>

          <MaintenanceSummary aircraft={ref.aircraft} bookings={liveBookings} bookingKinds={ref.bookingKinds} scopeIcaos={scopeIcaos} today={date} onPick={setSelected} />

          {!canWorkflow && (
            <div style={{ background: C.accentLight, border: `1px solid ${C.accentBorder}`, borderRadius: 8, padding: 12, fontSize: 10, color: C.accent, lineHeight: 1.5 }}>ℹ Relocate and bulk series actions are available to ops controllers and admins. Approve, decline and cancel are open to all staff.</div>
          )}
        </div>
      </div>

      {/* MOBILE OVERVIEW (Option B) — floating button → bottom-sheet drawer with This Day / Staff /
          Aircraft tabs. All tabs are client-side views over data already loaded above (ref.* + the
          day's bookings); no new endpoints. Desktop keeps the inline right panel. */}
      {isMobile && !overviewOpen && (
        <button
          onClick={() => setOverviewOpen(true)}
          style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 140, background: C.navy, color: '#fff', border: 'none', borderRadius: 999, padding: '12px 20px', fontSize: 13, fontWeight: 700, fontFamily: F, boxShadow: '0 6px 20px rgba(0,0,0,0.28)', cursor: 'pointer' }}
        >
          Overview
        </button>
      )}
      {isMobile && overviewOpen && (
        <>
          <div onClick={() => setOverviewOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 145 }} />
          <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 146, background: C.surface, borderRadius: '16px 16px 0 0', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.25)', fontFamily: F }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, letterSpacing: '0.06em' }}>OVERVIEW{allBases ? ' · ALL BASES' : selectedBase ? ` · ${selectedBase.name.toUpperCase()}` : ''}</div>
              <button onClick={() => setOverviewOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: C.textMuted, cursor: 'pointer' }}>✕</button>
            </div>
            {/* Tab bar (horizontally scrollable) */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>
              {([['day', 'This Day'], ['staff', 'Staff'], ['aircraft', 'Aircraft']] as const).map(([k, l]) => (
                <button key={k} onClick={() => setOverviewTab(k)}
                  style={{ whiteSpace: 'nowrap', minHeight: 38, border: `1px solid ${overviewTab === k ? C.accent : C.border}`, background: overviewTab === k ? C.accent : C.surface, color: overviewTab === k ? '#fff' : C.textDim, borderRadius: 999, padding: '6px 16px', fontSize: 12, fontWeight: 700, fontFamily: F, cursor: 'pointer' }}>
                  {l}
                </button>
              ))}
            </div>
            {/* Body */}
            <div style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 12 }}>
              {overviewTab === 'day' && (
                <>
                  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, letterSpacing: '0.08em', marginBottom: 10 }}>{viewMode === 'day' ? 'THIS DAY' : 'THIS WEEK'}{allBases ? ' · ALL BASES' : ''}</div>
                    {statRows.map(([l, v, c]) => (
                      <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: C.textMuted }}>{l}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: c, fontFamily: F }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <MaintenanceSummary aircraft={ref.aircraft} bookings={liveBookings} bookingKinds={ref.bookingKinds} scopeIcaos={scopeIcaos} today={date} onPick={(b) => { setOverviewOpen(false); setSelected(b); }} />
                </>
              )}
              {overviewTab === 'staff' && (() => {
                const list = ref.staff.filter((s) => allBases || (s.current_base_id ?? s.home_base_id) === selectedBase?.id);
                if (list.length === 0) return <div style={{ fontSize: 12, color: C.textMuted, padding: 8 }}>No instructors in scope.</div>;
                return list.map((s) => {
                  const mine = dayBookings.filter((b) => b.participants.some((p) => p.user_id === s.id));
                  const unavail = mine.some((b) => b.booking_kind_id && lookups.bookingKinds.get(b.booking_kind_id)?.is_unavailability);
                  const load = unavail ? 'unavailable' : mine.length > 0 ? `${mine.length} session${mine.length === 1 ? '' : 's'}` : 'free';
                  const baseLabel = allBases ? ref.bases.find((b) => b.id === (s.current_base_id ?? s.home_base_id))?.short_code : null;
                  const sub = [s.pilot_code, s.role].filter(Boolean).join(' · ');
                  return (
                    <button key={s.id} onClick={() => focusResource(`ops-staff-${s.id}`)} style={overviewRowStyle}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{s.preferred_name ?? s.full_name}</div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>{sub || '—'}{baseLabel ? ` · ${baseLabel}` : ''}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: unavail ? C.amber : mine.length ? C.accent : C.textMuted, whiteSpace: 'nowrap' }}>{load}</span>
                    </button>
                  );
                });
              })()}
              {overviewTab === 'aircraft' && (() => {
                const today = date; // M3: the Aircraft overview badge follows the VIEWED day, matching the row hatch + MaintenanceSummary
                const list = ref.aircraft.filter((a) => !scopeIcaos || (!!a.current_base_icao && scopeIcaos.has(a.current_base_icao)));
                if (list.length === 0) return <div style={{ fontSize: 12, color: C.textMuted, padding: 8 }}>No aircraft in scope.</div>;
                return list.map((a) => {
                  const st = aircraftStatus(a, today);
                  const count = dayBookings.filter((b) => b.asset_allocations.some((al) => al.aircraft_registration === a.registration)).length;
                  const sub = [a.model ?? a.aircraft_category, a.current_base_icao].filter(Boolean).join(' · ');
                  return (
                    <button key={a.registration} onClick={() => focusResource(`ops-ac-${a.registration}`)} style={overviewRowStyle}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F }}>{a.registration}</div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>{sub || '—'}</div>
                        {st.note && <div style={{ fontSize: 10, color: C.purple }}>⚠ {st.note}</div>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: st.color, background: st.bg, borderRadius: 999, padding: '2px 8px' }}>{st.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: count ? C.accent : C.textMuted }}>{count ? `${count} booking${count === 1 ? '' : 's'}` : '—'}</span>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        </>
      )}

      {showOutside && (
        <OutsideHoursPopup bookings={outsideBookings} staff={lookups.staff} students={lookups.students} bases={lookups.bases} allBases={allBases} onPick={(b) => { setShowOutside(false); setSelected(b); }} onClose={() => setShowOutside(false)} />
      )}

      {showWaitlist && (
        <WaitlistPopup bookings={visibleWaitlist} staff={lookups.staff} students={lookups.students} bases={lookups.bases} allBases={allBases} onPick={(b) => { setShowWaitlist(false); setSelected(b); }} onClose={() => setShowWaitlist(false)} />
      )}

      {selected && (
        <BookingDetail booking={selected} lookups={lookups} reasonCodes={reasonCodes} canWorkflow={canWorkflow} canApprove={canApprove} canBook={canBook} canCancel={canCancel} canMarkAdvised={canMarkAdvised} canEditMaintenance={canEditMaintenance} settings={ref.settings}
          openConflictId={conflictByTask.get(selected.id) ?? null}
          onResolveConflict={(id) => setResolverConflictId(id)}
          onClose={() => setSelected(null)} onEdit={openEdit} onCopy={openCopy}
          onSeriesAction={(b, action) => setSeriesAction({ booking: b, action })}
          onChanged={onWorkflowChanged} />
      )}

      {/* Recurring Phase 3 — bulk series action (edit all/following, cancel, end) with step-up. The
          counted impact = future un-overridden, non-cancelled occurrences from the boundary (today,
          or the occurrence's slot for "following"). */}
      {seriesAction && ref && (() => {
        const { booking, action } = seriesAction;
        const todayStr = new Date().toISOString().slice(0, 10);
        const from = action.kind !== 'end' && action.scope === 'following'
          ? (booking.series_occurrence_date ?? todayStr) : todayStr;
        const impact = displayBookings.filter(
          (b) => b.series_id === booking.series_id && !b.series_overridden
            && b.booking_status !== 'cancelled' && b.series_occurrence_date != null
            && b.series_occurrence_date >= from,
        ).length;
        return (
          <SeriesActionModal
            booking={booking} action={action} impact={impact}
            bases={ref.bases} activities={ref.activities} bookingKinds={ref.bookingKinds} reasonCodes={reasonCodes}
            settings={ref.settings}
            onClose={() => setSeriesAction(null)}
            onDone={(msg) => { setSeriesAction(null); setSelected(null); setWarnings([msg]); void loadRefData(); loadBookings(); }}
          />
        );
      })()}

      {resolverConflictId && (
        <ConflictResolver
          conflictId={resolverConflictId}
          canWorkflow={canWorkflow}
          online={connectionOnline}
          onClose={() => setResolverConflictId(null)}
          onResolved={() => { void loadRefData(); loadBookings(); void refreshOpenConflicts(); }}
        />
      )}

      {/* Own-booking conflict interrupt — shared notifications filtered to this user's tasks,
          suppressed while any form/modal is open. Extra salience beyond the global header bell. */}
      <OwnConflictPopup
        notifications={notifications}
        ownTaskIds={ownTaskIds}
        suppress={showForm || !!editing || !!selected || !!relocateModal || !!relocatePrompt || !!resolverConflictId}
        onOpenConflict={(id) => setResolverConflictId(id)}
        onRead={(id) => { void markRead(id); }}
        onDismiss={(id) => { void dismiss(id); }}
      />
    </div>
  );
}

const navBtn: React.CSSProperties = {
  width: 26, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
  color: '#fff', borderRadius: 5, padding: '3px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F,
};

// ── Mobile toolbar dropdowns (2026-07-23 header rework; FilterMultiSelect pattern from
// Enquiries/Tasks: trigger pill + white popover, close on outside tap / Esc / selection) ──────

const mobilePill: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.25)', color: '#fff', borderRadius: 7, padding: '9px 12px',
  fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F, whiteSpace: 'nowrap',
};
const mobileMenuRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
  background: 'none', border: 'none', padding: '11px 10px', fontSize: 13, fontWeight: 600,
  color: C.text, cursor: 'pointer', borderRadius: 6, fontFamily: F,
};

// Shared popover-close behaviour (outside tap + Esc), same shape as FilterMultiSelect's.
function usePopoverClose(open: boolean, wrapRef: React.RefObject<HTMLDivElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, wrapRef, close]);
}

// Base picker — SINGLE-select (baseId is one value: ALL_BASES or one base id), so this is a
// one-pick menu that closes on selection, NOT the multi-checkbox kind. Each base keeps its
// ui_color as the selected-pill colour and a dot in the menu.
function MobileBaseSelect({ bases, baseId, onPick }: { bases: Base[]; baseId: string; onPick: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  usePopoverClose(open, wrapRef, () => setOpen(false));

  const allBases = baseId === ALL_BASES;
  const current = allBases ? null : bases.find((b) => b.id === baseId);
  const pillColor = current?.ui_color ?? C.accent;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox" aria-expanded={open} aria-label="Choose base"
        title="Choose base"
        style={{ ...mobilePill, background: pillColor, borderColor: pillColor }}
      >
        {current?.name ?? 'All bases'} <span style={{ fontSize: 10, opacity: 0.85 }}>▾</span>
      </button>
      {open && (
        <div role="listbox" aria-label="Base" style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60, minWidth: 190,
          background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 4,
        }}>
          <button
            type="button" role="option" aria-selected={allBases}
            onClick={() => { onPick(ALL_BASES); setOpen(false); }}
            style={{ ...mobileMenuRow, ...(allBases ? { background: '#eff6ff', color: C.accent } : {}) }}
          >
            <span aria-hidden="true">🌐</span> All bases {allBases && <span style={{ marginLeft: 'auto' }}>✓</span>}
          </button>
          {bases.map((b) => {
            const sel = baseId === b.id;
            return (
              <button
                key={b.id} type="button" role="option" aria-selected={sel}
                onClick={() => { onPick(b.id); setOpen(false); }}
                style={{ ...mobileMenuRow, ...(sel ? { background: '#eff6ff', color: C.accent } : {}) }}
              >
                <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 5, background: b.ui_color, flexShrink: 0 }} />
                {b.name} {sel && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// "Show" — MULTI-select checklist over the independent row-filter toggles (+ Show cancelled for
// the workflow tier). Stays open across toggles (it is a set of switches, not a pick).
function MobileShowSelect({ filters, onToggleFilter, showCancelled, onToggleCancelled, canWorkflow }: {
  filters: RowFilters; onToggleFilter: (k: keyof RowFilters) => void;
  showCancelled: boolean; onToggleCancelled: () => void; canWorkflow: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  usePopoverClose(open, wrapRef, () => setOpen(false));

  const activeCount =
    (['aircraft', 'instructors', 'assets'] as (keyof RowFilters)[]).filter((k) => filters[k]).length +
    (canWorkflow && showCancelled ? 1 : 0);
  const checkRow: React.CSSProperties = { ...mobileMenuRow, cursor: 'pointer' };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true" aria-expanded={open} aria-label="Choose what to show"
        title="Choose what to show"
        style={mobilePill}
      >
        Show: {activeCount} <span style={{ fontSize: 10, opacity: 0.85 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 60, minWidth: 190,
          background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 4,
        }}>
          {(['aircraft', 'instructors', 'assets'] as (keyof RowFilters)[]).map((k) => (
            <label key={k} style={{ ...checkRow, textTransform: 'capitalize' }}>
              <input type="checkbox" checked={filters[k]} onChange={() => onToggleFilter(k)} style={{ width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' }} />
              {k}
            </label>
          ))}
          {canWorkflow && (
            <label style={checkRow}>
              <input type="checkbox" checked={showCancelled} onChange={onToggleCancelled} style={{ width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' }} />
              Show cancelled
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// Mobile Overview drawer — Staff/Aircraft list row.
const overviewRowStyle: React.CSSProperties = {
  display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 8,
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px',
  marginBottom: 6, textAlign: 'left', cursor: 'pointer', fontFamily: F,
};
